import { navigate } from 'gatsby-link';
import { omit } from 'lodash-es';
import { ofType } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';

import { createFlashMessage } from '@components/Flash/redux';
import { FlashMessages } from '@components/Flash/redux/flash-messages';
import {
  catchError,
  filter,
  map,
  mergeMap,
  retry,
  startWith,
  switchMap
} from 'rxjs/operators';
import { challengeTypes, submitTypes } from '../../../../utils/challenge-types';
import {
  fetchUserComplete,
  hardGoTo,
  submitComplete,
  updateComplete,
  updateFailed
} from '../../../redux/actions';
import { isSignedInSelector, userSelector } from '../../../redux/selectors';
import { getSessionUser, mapFilesToChallengeFiles } from '../../../utils/ajax';
import {
  standardizeRequestBody,
  validateBodySize
} from '../../../utils/challenge-request-helpers';
import postUpdate$ from '../utils/post-update';
import { actionTypes } from './action-types';
import { setCompletedChallengesLoading } from './actions';
import {
  challengeFilesSelector,
  challengeMetaSelector,
  challengeTestsSelector,
  projectFormValuesSelector
} from './selectors';

function postChallenge(update, username, challengeType, challengeFiles = []) {
  return postUpdate$(update).pipe(
    retry(3),
    switchMap(({ data }) => {
      if (data.type === 'error') {
        const flashMessage =
          data.code === 'PAYLOAD_SIZE_LIMIT_EXCEEDED'
            ? {
                message: FlashMessages.PayloadSizeLimitExceeded,
                type: 'danger',
                variables: {
                  'current-size': (data.details?.currentSize / 1024).toFixed(2),
                  'max-size': (data.details?.maxAllowed / 1024).toFixed(0),
                  dismissible: true
                }
              }
            : {
                message: FlashMessages.SubmitError,
                type: 'error',
                variables: { dismissible: true }
              };

        return of(
          updateFailed(update),
          setCompletedChallengesLoading(false),
          createFlashMessage(flashMessage)
        );
      }

      const { savedChallenges, points } = data;
      const payloadWithClientProperties = {
        ...omit(update.payload, ['files'])
      };
      if (update.payload.files) {
        payloadWithClientProperties.challengeFiles = update.payload.files.map(
          ({ key, ...rest }) => ({
            ...rest,
            fileKey: key
          })
        );
      }

      const isLastExerciseMundoTech =
        update.payload.id === '66fbe66d4368241d29757851';

      const completionMessage =
        challengeType === challengeTypes.videoOnly ||
        challengeType === challengeTypes.textOnly
          ? 'Parabéns! + 10 pontos'
          : FlashMessages.CongratsChallengeComplete;

      return from(getSessionUser()).pipe(
        map(session => {
          const {
            data: { user = {}, result = '' }
          } = session;

          const refreshedUser = user[result] || {};

          return [
            submitComplete({
              submittedChallenge: {
                username,
                points,
                ...payloadWithClientProperties
              },
              savedChallenges: mapFilesToChallengeFiles(savedChallenges)
            }),

            fetchUserComplete({
              user: refreshedUser,
              username: result
            }),

            updateComplete(),
            setCompletedChallengesLoading(false),

            createFlashMessage({
              message: completionMessage,
              type: 'success',
              variables: { dismissible: true },
              challengeFiles
            }),

            isLastExerciseMundoTech && hardGoTo('/learn')
          ].filter(Boolean);
        }),
        mergeMap(actions => actions)
      );
    }),
    catchError(() =>
      of(
        updateFailed(update),
        setCompletedChallengesLoading(false),
        createFlashMessage({
          message:
            'Erro ao registrar resposta. Tente novamente mais tarde ou contate a equipe de suporte.',
          type: 'error',
          variables: { dismissible: true }
        })
      )
    ),
    startWith(setCompletedChallengesLoading(true))
  );
}

function submitModern(type, state) {
  const challengeType = state.challenge.challengeMeta.challengeType;

  const tests = challengeTestsSelector(state);
  if (
    challengeType === 11 ||
    challengeType === 15 ||
    (tests.length > 0 && tests.every(test => test.pass && !test.err))
  ) {
    if (type === actionTypes.checkChallenge) {
      return of({ type: 'this was a check challenge' });
    }

    if (type === actionTypes.submitChallenge) {
      const { id, block } = challengeMetaSelector(state);
      const challengeFiles = challengeFilesSelector(state);
      const { username } = userSelector(state);

      let body;

      if (
        block === 'javascript-algorithms-and-data-structures-projects' ||
        challengeType === challengeTypes.multifileCertProject
      ) {
        body = standardizeRequestBody({ id, challengeType, challengeFiles });
      } else {
        const { files } = standardizeRequestBody({
          id,
          challengeType,
          challengeFiles
        });

        body = files ? { id, files, challengeType } : { id, challengeType };
      }

      const validationError = validateBodySize(body);
      if (validationError) {
        return validationError;
      }

      const update = {
        endpoint: '/modern-challenge-completed',
        payload: body
      };

      return postChallenge(update, username, challengeType, challengeFiles);
    }
  }

  return EMPTY();
}

function submitVideo(type, state) {
  const challengeType = state.challenge.challengeMeta.challengeType;
  if (type === actionTypes.checkChallenge) {
    return of({ type: 'this was a check challenge' });
  }

  if (type === actionTypes.submitChallenge) {
    const { id, block } = challengeMetaSelector(state);
    const challengeFiles = challengeFilesSelector(state);
    const { username } = userSelector(state);

    let body;
    if (
      block === 'javascript-algorithms-and-data-structures-projects' ||
      challengeType === challengeTypes.multifileCertProject
    ) {
      body = standardizeRequestBody({ id, challengeType, challengeFiles });
    } else {
      body = {
        id,
        challengeType
      };
    }

    const validationError = validateBodySize(body);
    if (validationError) {
      return validationError;
    }

    const update = {
      endpoint: '/modern-challenge-completed',
      payload: body
    };
    if (id === '66fbe66d4368241d29757851') {
      // Terminou o mundoTech
      navigate('/learn');
    }
    return postChallenge(update, username, challengeType, []);
  }
  return EMPTY();
}

function submitProject(type, state) {
  if (type === actionTypes.checkChallenge) {
    return EMPTY();
  }

  const { solution, githubLink } = projectFormValuesSelector(state);
  const { id, challengeType } = challengeMetaSelector(state);
  const { username } = userSelector(state);
  const challengeInfo = { id, challengeType, solution };
  if (challengeType === challengeTypes.backEndProject) {
    challengeInfo.githubLink = githubLink;
  }

  const validationError = validateBodySize(challengeInfo);
  if (validationError) {
    return validationError;
  }

  const update = {
    endpoint: '/project-completed',
    payload: challengeInfo
  };
  return postChallenge(update, username, challengeType);
}

function submitBackendChallenge(type, state) {
  const tests = challengeTestsSelector(state);
  if (tests.length > 0 && tests.every(test => test.pass && !test.err)) {
    if (type === actionTypes.submitChallenge) {
      const { id, challengeType } = challengeMetaSelector(state);
      const { username } = userSelector(state);
      const {
        solution: { value: solution }
      } = projectFormValuesSelector(state);
      const challengeInfo = { id, solution };

      const validationError = validateBodySize(challengeInfo);
      if (validationError) {
        return validationError;
      }

      const update = {
        endpoint: '/backend-challenge-completed',
        payload: challengeInfo
      };
      return postChallenge(update, username, challengeType, []);
    }
  }
  return EMPTY();
}

const submitters = {
  tests: submitModern, // Videos challenges with quiz
  backend: submitBackendChallenge, // NOT IN USE
  noTests: submitVideo, // Videos and text challenges
  'project.frontEnd': submitProject, // Submit github project
  'project.backEnd': submitProject // NOT IN USE
};

export default function completionEpic(action$, state$) {
  return action$.pipe(
    ofType(actionTypes.submitChallenge),
    switchMap(({ type }) => {
      const state = state$.value;

      const { challengeType } = challengeMetaSelector(state);

      let submitter = () => of({ type: 'no-user-signed-in' });
      if (
        !(challengeType in submitTypes) ||
        !(submitTypes[challengeType] in submitters)
      ) {
        throw new Error(
          'Unable to find the correct submit function for challengeType ' +
            challengeType
        );
      }

      if (isSignedInSelector(state)) {
        submitter = submitters[submitTypes[challengeType]];
      }
      return submitter(type, state).pipe(filter(Boolean));
    })
  );
}
